import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FiChevronLeft, FiChevronRight, FiDownload, FiUploadCloud, FiCheckCircle, FiXCircle } from 'react-icons/fi';

import {
  addWeeks, endOfISOWeek, format, getISOWeek, getISOWeekYear, parseISO, startOfISOWeek,
} from 'date-fns';

import invoicingService from '../../services/api/invoicingService';
import SyncMatchDialog from '../../components/admin/SyncMatchDialog';
import LegacyInvoiceReconciliation from './LegacyInvoiceReconciliation';

const Page = styled.div`width: 100%; max-width: 1000px; min-width: 0;`;
const Title = styled.h1`font-size: 1.5rem; color: var(--ve-ink-800); margin: 0 0 0.25rem;`;
const Subtitle = styled.p`color: var(--ve-ink-600); font-size: 0.9rem; margin: 0 0 1.25rem;`;
const Card = styled.div`background: #fff; border-radius: 0.5rem; box-shadow: 0 1px 3px rgba(0,0,0,0.08); padding: 1.25rem 1.5rem; margin-bottom: 1.25rem; @media (max-width: 600px) { padding: 1rem; }`;
const CardTitle = styled.h2`font-size: 1.05rem; color: var(--ve-ink-800); margin: 0 0 0.75rem;`;
const Bar = styled.div`display: flex; align-items: center; gap: 1rem; flex-wrap: wrap; justify-content: space-between;`;
const MonthNav = styled.div`
  display: grid; grid-template-columns: 2.5rem auto 2.5rem auto; align-items: end; gap: 0.5rem;
  @media (max-width: 600px) {
    width: 100%; grid-template-columns: 2.5rem minmax(0, 1fr) 2.5rem;
    > :last-child { grid-column: 1 / -1; }
  }
`;
// Bare buttons inherit white primary-action text globally, including on hover.
// Navigation needs its own foreground/background pair for both states.
const NavBtn = styled.button`
  display: inline-flex; align-items: center; justify-content: center; gap: 0.4rem;
  height: 2.5rem; padding: 0.45rem 0.7rem; border: 1px solid var(--border-subtle);
  border-radius: 0.25rem; background: #fff; color: var(--ve-ink-800); font-size: 0.9rem;
  white-space: nowrap; cursor: pointer;
  &:hover { background: var(--surface-subtle); color: var(--ve-ink-800); }
`;
const MonthLabel = styled.span`font-weight: 600; color: var(--ve-ink-800); align-self: center; text-align: center;`;
const Check = styled.label`
  display: inline-flex; align-items: center; gap: 0.4rem; margin: 0; min-width: 0;
  font-size: 0.9rem; color: var(--ve-ink-800); cursor: pointer; overflow-wrap: anywhere;
  input { flex-shrink: 0; }
`;
const ActivationBar = styled.div`display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; margin-bottom: 1rem;`;
const SelectionHint = styled.span`font-size: 0.85rem; color: var(--ve-ink-600);`;
const CustBlock = styled.div`border: 1px solid #eef1f3; border-radius: 0.4rem; margin-bottom: 0.85rem; overflow: hidden;`;
const CustHead = styled.div`display: flex; justify-content: space-between; align-items: center; gap: 0.5rem 1rem; flex-wrap: wrap; padding: 0.6rem 1rem; background: var(--surface-subtle); font-weight: 600; color: var(--ve-ink-800);`;
const EmpRow = styled.div`padding: 0.4rem 1rem 0.4rem 1.75rem; border-top: 1px solid #eef1f3;`;
const EmpHead = styled.div`display: flex; justify-content: space-between; align-items: center; font-size: 0.9rem; color: #34495e; font-weight: 600;`;
const EntryRow = styled.div`display: grid; grid-template-columns: 1.6rem 1fr 1fr 0.7fr auto; gap: 0.5rem; align-items: center; padding: 0.25rem 0 0.25rem 1.5rem; font-size: 0.82rem; color: var(--ve-ink-600);`;
// Horizontal scroll wrapper so the nested 5-col entry grid stays usable on narrow screens.
const EntryScroll = styled.div`overflow-x: auto;`;
const EntryInner = styled.div`min-width: 520px;`;
const Badge = styled.span`font-size: 0.65rem; background: #fef5e7; color: #b9770e; padding: 0.05rem 0.4rem; border-radius: 999px;`;
const Total = styled.div`display: flex; justify-content: flex-end; gap: 1rem; padding: 0.75rem 1rem; font-weight: 700; color: var(--ve-ink-800);`;
const Btn = styled.button`display: inline-flex; align-items: center; gap: 0.4rem; padding: 0.55rem 1.1rem; border-radius: 0.25rem; font-size: 0.9rem; font-weight: 600; cursor: pointer; border: none; color: #fff; background: ${({ $variant }) => ($variant === 'ghost' ? 'var(--ve-ink-600)' : $variant === 'download' ? 'var(--act-primary)' : 'var(--act-primary)')}; &:hover { filter: brightness(0.94); } &:disabled { opacity: 0.5; cursor: not-allowed; }`;
const Actions = styled.div`display: flex; gap: 0.75rem; margin-top: 1rem; flex-wrap: wrap; align-items: center;`;
const Alert = styled.div`padding: 0.75rem 1rem; border-radius: 0.25rem; margin-bottom: 1rem; font-size: 0.9rem; background: ${({ $type }) => ($type === 'error' ? 'var(--status-danger-plate)' : '#eafaf1')}; color: ${({ $type }) => ($type === 'error' ? 'var(--act-danger)' : '#1e8449')}; border: 1px solid ${({ $type }) => ($type === 'error' ? '#f5c6cb' : '#abebc6')};`;
const ResultRow = styled.div`display: flex; align-items: center; gap: 0.4rem; font-size: 0.85rem; color: ${({ $ok }) => ($ok ? '#1e8449' : 'var(--act-danger)')};`;
const Empty = styled.div`padding: 1.5rem; text-align: center; color: var(--ve-ink-600);`;
const ModeTabs = styled.div`display: inline-flex; border: 1px solid var(--border-subtle); border-radius: 0.25rem; overflow: hidden;`;
const ModeTab = styled.button`
  padding: 0.45rem 0.9rem; border: none; cursor: pointer; font-size: 0.85rem; font-weight: 600;
  background: ${({ $active }) => ($active ? 'var(--act-primary)' : '#fff')};
  color: ${({ $active }) => ($active ? '#fff' : 'var(--ve-ink-600)')};
  &:hover {
    background: ${({ $active }) => ($active ? 'var(--act-primary)' : 'var(--surface-subtle)')};
    color: ${({ $active }) => ($active ? '#fff' : 'var(--ve-ink-600)')};
  }
`;
const PeriodRow = styled.div`display: flex; align-items: flex-end; gap: 1rem; flex-wrap: wrap; margin-top: 0.9rem;`;
const Field = styled.label`
  display: flex; flex-direction: column; gap: 0.25rem; margin: 0; min-width: 0;
  font-size: 0.78rem; color: var(--ve-ink-600); font-weight: 600;
  @media (max-width: 600px) { flex: 1 1 8.5rem; }
`;
const DateInput = styled.input`height: 2.5rem; min-width: 0; padding: 0.42rem 0.6rem; border: 1px solid var(--border-subtle); border-radius: 0.25rem; background: #fff; color: var(--ve-ink-800); font-size: 0.9rem; font-family: inherit;`;
const WeekInput = styled(DateInput)`width: 5.5rem; @media (max-width: 600px) { width: 100%; }`;
const PeriodHint = styled.div`font-size: 0.8rem; color: var(--ve-ink-600); margin-top: 0.75rem;`;

// Native indeterminate state exposes partial selections visually and to assistive technology.
const BulkCheckbox = ({ indeterminate, ...props }) => (
  <input
    {...props}
    type="checkbox"
    ref={(node) => { if (node) node.indeterminate = Boolean(indeterminate); }}
  />
);

const BillingHistory = ({ data }) => {
  const publications = data.current || data.publications || [];
  if (!publications.length) return <div>No CRM billing schedule has been received. Check financial synchronization in CRM.</div>;
  return <div>{publications.map((publication, index) => <div key={publication.id || index} style={{ margin: '0.5rem 0' }}>
    <strong>Placement {publication.placementId} · applied generation {publication.generation}</strong>
    <div><small>Applied {publication.appliedAt} · receipt {publication.receiptId || 'historical publication'}</small></div>
    {(publication.blockers || []).map((blocker, blockerIndex) => <div key={blockerIndex}><small>{blocker.code}: {blocker.remediation}</small></div>)}
    {(publication.terms || publication.schedule?.terms || []).map((term, termIndex) => <div key={term.id || term.revisionId || termIndex} style={{ fontSize: '0.82rem' }}>
      {term.effectiveFrom} – {term.effectiveToExclusive ? `${term.effectiveToExclusive} (exclusive)` : 'open'}: {term.amount ?? term.rate?.amount ?? 'Policy'} {term.currency ?? term.rate?.currency} / {term.unit ?? term.rate?.unit}; revision {term.revisionId || term.sourceRevisionId}; source {term.authority || term.sourceSystem || publication.sourceSystem} · {term.sourceRevisionId} · evidence {term.sourceEvidenceRef}
    </div>)}
    {publication.removedRevisionIds?.length > 0 && <div><small>Removed/revoked revisions: {publication.removedRevisionIds.join(', ')}</small></div>}
  </div>)}{(data.history || []).length > 0 && <details><summary>Publication history ({data.history.length})</summary>{data.history.map((item, index) => <div key={item.id || index}><small>Placement {item.placementId} · generation {item.generation} · {item.appliedAt}</small>{(item.terms || []).map((term, termIndex) => <div key={termIndex}><small>{term.effectiveFrom} – {term.effectiveToExclusive || 'open'}: {term.amount} {term.currency}/{term.unit} · {term.revisionId} · {term.authority} · source {term.sourceRevisionId}</small></div>)}</div>)}</details>}</div>;
};

const Invoicing = () => {
  const { t, i18n } = useTranslation();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [includePrevious, setIncludePrevious] = useState(false);
  // Vikar Experten invoices week by week, so the week picker is the default.
  // The week number only PREFILLS from/to; the dates are what actually filter,
  // and either end can be adjusted by hand afterwards.
  const [mode, setMode] = useState('week');
  const [from, setFrom] = useState(() => format(startOfISOWeek(new Date()), 'yyyy-MM-dd'));
  const [to, setTo] = useState(() => format(endOfISOWeek(new Date()), 'yyyy-MM-dd'));
  const [activation, setActivation] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [savingAct, setSavingAct] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [results, setResults] = useState(null);
  const [syncData, setSyncData] = useState(null); // sync-preview result -> match dialog
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [prepared, setPrepared] = useState(null);
  const [preparedEntryIds, setPreparedEntryIds] = useState([]);
  const [draftNumbers, setDraftNumbers] = useState({});
  const [billingHistory, setBillingHistory] = useState({});
  const [correctionReview, setCorrectionReview] = useState(null);

  // In week mode the activation context follows the period's FIRST day, so a
  // week spanning a month boundary is administered under the month it starts in.
  // A date input can be cleared, which would otherwise send NaN as the period
  // and turn a normal edit into a failed request. An incomplete interval simply
  // keeps showing the last good one until both ends are set again.
  const rangeReady = mode === 'week' && /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(to) && from <= to;
  const periodYear = rangeReady ? Number(from.slice(0, 4)) : year;
  const periodMonth = rangeReady ? Number(from.slice(5, 7)) : month;
  const range = rangeReady ? { from, to } : null;

  const load = useCallback(async () => {
    try {
      setLoading(true); setError(null); setResults(null); setPrepared(null);
      const [act, prev] = await Promise.all([
        invoicingService.getActivation(periodYear, periodMonth),
        invoicingService.getPreview(periodYear, periodMonth, includePrevious, range),
      ]);
      setActivation(act);
      setPreview(prev);
      const ids = new Set();
      prev.customers.forEach((c) => c.employees.forEach((e) => e.entries.forEach((en) => ids.add(en.id))));
      setSelected(ids);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodYear, periodMonth, includePrevious, rangeReady, from, to]);

  useEffect(() => { load(); }, [load]);

  const shiftMonth = (d) => {
    let m = month + d; let y = year;
    if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y); setNotice(null);
  };
  const monthLabel = new Date(year, month - 1, 1).toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' });

  // The week the range currently starts in. Shown as the week number, and left
  // as-is when the admin has adjusted the dates away from a clean week.
  const weekNo = getISOWeek(parseISO(from));
  const weekYear = getISOWeekYear(parseISO(from));
  const isWholeWeek = from === format(startOfISOWeek(parseISO(from)), 'yyyy-MM-dd')
    && to === format(endOfISOWeek(parseISO(from)), 'yyyy-MM-dd');

  const applyWeekOf = (date) => {
    setFrom(format(startOfISOWeek(date), 'yyyy-MM-dd'));
    setTo(format(endOfISOWeek(date), 'yyyy-MM-dd'));
    setNotice(null);
  };
  const shiftWeek = (delta) => applyWeekOf(addWeeks(parseISO(from), delta));
  const setWeekNumber = (raw) => {
    const n = parseInt(raw, 10);
    if (!Number.isInteger(n) || n < 1 || n > 53) return;
    // Anchor on 4 January, which is always in ISO week 1, then step n-1 weeks.
    applyWeekOf(addWeeks(startOfISOWeek(new Date(weekYear, 0, 4)), n - 1));
  };

  const toggleEntry = (id) => { setPrepared(null); setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); };

  const setCustomerActive = (customerId, active) => {
    setActivation((prev) => ({
      ...prev,
      customers: prev.customers.map((c) => (c.customerId === customerId ? { ...c, active } : c)),
    }));
  };
  const setEmployeeActive = (customerId, employeeId, active) => {
    setActivation((prev) => ({
      ...prev,
      customers: prev.customers.map((c) =>
        c.customerId === customerId
          ? { ...c, employees: c.employees.map((e) => (e.employeeId === employeeId ? { ...e, active } : e)) }
          : c
      ),
    }));
  };

  const activeCustomers = activation?.customers || [];
  const allActive = activeCustomers.length > 0
    && activeCustomers.every((c) => c.active && c.employees.every((e) => e.active));
  // Employee preferences retained under an inactive customer do not count as included.
  const someActive = activeCustomers.some((c) => c.active);
  const setAllActive = (active) => {
    setActivation((prev) => ({
      ...prev,
      customers: prev.customers.map((c) => ({
        ...c,
        active,
        employees: c.employees.map((e) => ({ ...e, active })),
      })),
    }));
  };
  const setAllEmployeesActive = (customerId, active) => {
    setActivation((prev) => ({
      ...prev,
      customers: prev.customers.map((c) => c.customerId === customerId
        ? { ...c, employees: c.employees.map((e) => ({ ...e, active })) }
        : c),
    }));
  };

  const saveActivation = async () => {
    if (!activation) return;
    try {
      setSavingAct(true); setError(null); setNotice(null);
      const customers = activation.customers.map((c) => ({ customerId: c.customerId, active: c.active }));
      const employees = [];
      activation.customers.forEach((c) => c.employees.forEach((e) => employees.push({ customerId: c.customerId, employeeId: e.employeeId, active: e.active })));
      await invoicingService.updateActivation(periodYear, periodMonth, { customers, employees });
      setNotice(t('admin.invoicing.activationSaved'));
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingAct(false);
    }
  };

  const download = async () => {
    try { setBusy(true); setError(null); await invoicingService.exportPreview([...selected]); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  // Customers involved in the current selection — the sync step only needs to
  // match those.
  const selectedCustomerIds = () => {
    if (!preview) return [];
    return preview.customers
      .filter((c) => c.employees.some((e) => e.entries.some((en) => selected.has(en.id))))
      .map((c) => c.customerId);
  };

  // Entry ids to push, excluding any belonging to customers the admin chose to
  // skip in the match dialog (skipCustomerIds). Defaults to all selected.
  const entryIdsForPush = (skipCustomerIds = new Set()) => {
    if (!preview) return [...selected];
    const excluded = new Set();
    preview.customers.forEach((c) => {
      if (skipCustomerIds.has(c.customerId)) {
        c.employees.forEach((e) => e.entries.forEach((en) => excluded.add(en.id)));
      }
    });
    return [...selected].filter((id) => !excluded.has(id));
  };

  const doPush = async (entryIds) => {
    try {
      setBusy(true); setError(null); setNotice(null);
      if (preview?.billingMode !== 'legacy') {
        const review = await invoicingService.prepare(periodYear, periodMonth, entryIds, range);
        setPrepared(review);
        setPreparedEntryIds(entryIds);
        return;
      }
      const res = await invoicingService.push(periodYear, periodMonth, entryIds, range);
      // Reload first (it clears results + toggles loading), THEN set results so
      // the outcome panel survives the reload.
      await load();
      setResults(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const executeReviewed = async () => {
    try {
      setBusy(true); setError(null);
      const ids = (prepared?.results || []).filter((r) => r.ok).map((r) => r.preparationId);
      const response = await invoicingService.push(periodYear, periodMonth, preparedEntryIds, range, ids);
      await load();
      setResults(response);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const reconcile = async (id) => {
    try {
      setBusy(true); setError(null);
      await invoicingService.reconcile(id, Number(draftNumbers[id]));
      await load(); setNotice('The existing e-conomic draft was verified. No invoice was resent.');
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const loadBillingHistory = async (customerId) => {
    try { setBillingHistory((current) => ({ ...current, [customerId]: { loading: true } }));
      const data = await invoicingService.financialTerms(customerId);
      setBillingHistory((current) => ({ ...current, [customerId]: data }));
    } catch (err) { setBillingHistory((current) => ({ ...current, [customerId]: { error: err.message } })); }
  };

  const reviewCorrection = async (id) => {
    try { setBusy(true); setError(null); setCorrectionReview(await invoicingService.reviewCorrection(id)); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  // Push = match customers against e-conomic first (CVR/email; e-conomic
  // customers are never overwritten — only linked or newly created), let the
  // admin resolve anything ambiguous in the dialog, THEN send the invoices.
  const push = async () => {
    try {
      setBusy(true); setError(null); setResults(null); setNotice(null); setSyncError(null);
      const sync = await invoicingService.syncPreview(selectedCustomerIds());
      const pending = sync.rows.filter((r) => r.status !== 'linked');
      if (pending.length === 0) {
        setBusy(false);
        await doPush(entryIdsForPush());
        return;
      }
      setSyncData(sync);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmSync = async (decisions) => {
    try {
      setSyncBusy(true); setSyncError(null);
      const toApply = decisions.filter((d) => d.action && d.action !== 'skip');
      if (toApply.length) {
        const res = await invoicingService.syncApply(toApply);
        const failed = (res.results || []).filter((r) => !r.ok);
        if (failed.length) {
          setSyncError(failed.map((f) => `${f.customerName || f.customerId}: ${f.message}`).join(' — '));
          return;
        }
      }
      // Records the admin skipped are left un-transferred: exclude their entries.
      const skipIds = new Set(decisions.filter((d) => d.action === 'skip').map((d) => d.customerId));
      setSyncData(null);
      await doPush(entryIdsForPush(skipIds));
    } catch (err) {
      setSyncError(err.message);
    } finally {
      setSyncBusy(false);
    }
  };

  if (loading) return <Empty>{t('common.loading')}</Empty>;

  return (
    <Page>
      <Title>{t('admin.invoicing.title')}</Title>
      <Subtitle>{t('admin.invoicing.subtitle')}</Subtitle>
      {error && <Alert $type="error">{error}</Alert>}
      {notice && <Alert $type="success">{notice}</Alert>}
      {preview?.billingMode === 'legacy' && <Alert $type="error">Legacy e-conomic product pricing is explicitly enabled. These invoices do not use CRM dated prices.</Alert>}

      {(preview?.readiness || []).length > 0 && <Card>
        <CardTitle>Weekly invoice checklist</CardTitle>
        {preview.readiness.map((row) => <div key={row.customerId} style={{ marginBottom: '0.7rem' }}>
          <strong>{activation?.customers.find((c) => c.customerId === row.customerId)?.customerName || `Customer ${row.customerId}`}: {row.ready ? 'Ready to prepare' : 'Needs attention'}</strong>
          {(row.blockers || []).map((blocker, index) => <div key={`${blocker.code}-${index}`} style={{ fontSize: '0.85rem' }}>{blocker.message} — {blocker.owner}. {blocker.action} <small>({blocker.code})</small></div>)}
        </div>)}
      </Card>}

      {(preview?.legacyPendingClaims?.claims || []).length > 0 && <Card>
        <CardTitle>Legacy invoice claims requiring investigation</CardTitle>
        <Subtitle>Owner: billing administrator. An invoice may exist in e-conomic. Check the provider evidence with the integration administrator. These entries remain reserved. Do not resend or clear them. Legacy claims do not have the frozen evidence required by the verified-draft reconciliation below.</Subtitle>
        {preview.legacyPendingClaims.claims.map((claim) => <div key={`${claim.claimRef}-${claim.customerId}`} style={{ marginBottom: '0.75rem' }}>
          Customer {claim.customerId} · {claim.claimRef} · {claim.entryCount} reserved entries · {claim.from} to {claim.to}
        </div>)}
        {preview.legacyPendingClaims.truncated && <p>The first 100 claims are shown. Ask the integration administrator to review the remaining claims.</p>}
      </Card>}

      {(preview?.unresolvedOperations || []).length > 0 && <Card>
        <CardTitle>Invoice outcomes requiring reconciliation</CardTitle>
        <Subtitle>Check the existing draft in e-conomic. Tempus verifies the saved operation before adopting it; no invoice is resent.</Subtitle>
        {preview.unresolvedOperations.map((operation) => operation.kind === 'legacy'
          ? <LegacyInvoiceReconciliation key={operation.id} operation={operation} onComplete={load} />
          : <div key={operation.id} style={{ marginBottom: '0.75rem' }}>
          <div>Customer {operation.customerId} · {operation.id}</div>
          <DateInput type="number" min="1" aria-label={`Draft number for ${operation.id}`} value={draftNumbers[operation.id] || ''} onChange={(event) => setDraftNumbers((current) => ({ ...current, [operation.id]: event.target.value }))} />
          <Btn onClick={() => reconcile(operation.id)} disabled={busy || !draftNumbers[operation.id]}>Verify existing draft</Btn>
        </div>)}
      </Card>}

      {(preview?.settlements || []).length > 0 && <Card>
        <details><summary>Monthly settlements due or requiring review ({preview.settlements.length})</summary>
          <Subtitle>Historical invoices may already contain these items. Review them before any manual correction. Missed months are not added automatically.</Subtitle>
          {preview.settlements.map((item) => <div key={`${item.customerId}-${item.month}`} style={{ marginBottom: '0.5rem' }}><strong>{item.customerName} · {item.month} · {item.status === 'due' ? 'Due' : 'Check missed settlement'}</strong><div>{item.message}</div></div>)}
        </details>
      </Card>}

      {(preview?.invoiceHistory || []).length > 0 && <Card>
        <details><summary>Recent frozen invoice history ({preview.invoiceHistory.length})</summary>
          {preview.invoiceHistory.map((invoice) => <div key={invoice.id} style={{ margin: '0.8rem 0' }}>
            <strong>Draft {invoice.draftNumber} · customer {invoice.customerId} · {invoice.requestSnapshot.period.from} – {invoice.requestSnapshot.period.to}</strong>
            {invoice.kind === 'legacy' ? <p>Legacy product pricing. Check the adopted draft in e-conomic for provider prices; no CRM pricing history is claimed.</p>
              : <><div>{(invoice.resultSnapshot.lines || []).map((line, index) => <div key={index}><small>{line.description}: {line.quantity} × {line.unitNetPrice} {invoice.resultSnapshot.currency} · {(line.revisionIds || []).join(', ')}</small></div>)}</div>
                <Btn onClick={() => reviewCorrection(invoice.id)} disabled={busy}>Review later price changes</Btn></>}
          </div>)}
        </details>
        {correctionReview && <div style={{ marginTop: '1rem' }}>
          <strong>Draft {correctionReview.draftNumber}: {correctionReview.changed ? 'A later pricing difference needs review' : 'No pricing difference'}</strong>
          <p>{correctionReview.message}</p>
          {correctionReview.changed && <EntryScroll><table><thead><tr><th>Original frozen lines</th><th>Current CRM proposal</th></tr></thead><tbody><tr>
            {['original', 'proposed'].map((side) => <td key={side} style={{ verticalAlign: 'top', paddingRight: '1rem' }}>{correctionReview[side].lines.map((line, index) => <div key={index}>{line.description}: {line.quantity} × {line.unitNetPrice} {correctionReview[side].currency}<br /><small>{(line.revisionIds || []).join(', ')}</small></div>)}</td>)}
          </tr></tbody></table></EntryScroll>}
          <small>Comparison record: {correctionReview.reviewId}</small>
        </div>}
      </Card>}

      <Card>
        <Bar>
          <ModeTabs role="tablist">
            <ModeTab type="button" role="tab" aria-selected={mode === 'week'} $active={mode === 'week'} onClick={() => { setMode('week'); setNotice(null); }}>
              {t('admin.invoicing.byWeek')}
            </ModeTab>
            <ModeTab type="button" role="tab" aria-selected={mode === 'month'} $active={mode === 'month'} onClick={() => { setMode('month'); setNotice(null); }}>
              {t('admin.invoicing.byMonth')}
            </ModeTab>
          </ModeTabs>

          {mode === 'month' && (
            <Check>
              <input type="checkbox" checked={includePrevious} onChange={(e) => setIncludePrevious(e.target.checked)} />
              {t('admin.invoicing.includePrevious')}
            </Check>
          )}
        </Bar>

        {mode === 'month' ? (
          <PeriodRow>
            <MonthNav>
              <NavBtn onClick={() => shiftMonth(-1)} aria-label={t('admin.invoicing.prevMonth')}><FiChevronLeft /></NavBtn>
              <MonthLabel>{monthLabel}</MonthLabel>
              <NavBtn onClick={() => shiftMonth(1)} aria-label={t('admin.invoicing.nextMonth')}><FiChevronRight /></NavBtn>
              <NavBtn onClick={() => { setYear(now.getFullYear()); setMonth(now.getMonth() + 1); }}>{t('admin.invoicing.thisMonth')}</NavBtn>
            </MonthNav>
          </PeriodRow>
        ) : (
          <>
            <PeriodRow>
              <MonthNav>
                <NavBtn onClick={() => shiftWeek(-1)} aria-label={t('admin.invoicing.previousWeek')}><FiChevronLeft /></NavBtn>
                <Field>
                  {t('admin.invoicing.weekNumber')}
                  <WeekInput
                    type="number"
                    min="1"
                    max="53"
                    value={weekNo}
                    onChange={(e) => setWeekNumber(e.target.value)}
                  />
                </Field>
                <NavBtn onClick={() => shiftWeek(1)} aria-label={t('admin.invoicing.nextWeek')}><FiChevronRight /></NavBtn>
                <NavBtn onClick={() => applyWeekOf(new Date())}>{t('admin.invoicing.thisWeek')}</NavBtn>
              </MonthNav>

              {/* The dates are the actual filter — the week number above only
                  fills them in, so either end can be adjusted freely. */}
              <Field>
                {t('admin.invoicing.from')}
                <DateInput type="date" value={from} max={to} onChange={(e) => { setFrom(e.target.value); setNotice(null); }} />
              </Field>
              <Field>
                {t('admin.invoicing.to')}
                <DateInput type="date" value={to} min={from} onChange={(e) => { setTo(e.target.value); setNotice(null); }} />
              </Field>
            </PeriodRow>
            <PeriodHint>
              {isWholeWeek
                ? t('admin.invoicing.weekHint', { week: weekNo, year: weekYear })
                : t('admin.invoicing.customRangeHint')}
            </PeriodHint>
          </>
        )}
      </Card>

      {/* Activation */}
      <Card>
        <CardTitle>{t('admin.invoicing.activation')}</CardTitle>
        <Subtitle>{t('admin.invoicing.activationHint')}</Subtitle>
        <ActivationBar>
          <Check>
            <BulkCheckbox
              checked={allActive}
              indeterminate={someActive && !allActive}
              disabled={savingAct || activeCustomers.length === 0}
              aria-describedby="invoicing-all-hint"
              onChange={(e) => setAllActive(e.target.checked)}
            />
            {t('admin.invoicing.all')}
          </Check>
          <SelectionHint id="invoicing-all-hint">{t('admin.invoicing.allHint')}</SelectionHint>
        </ActivationBar>
        {activeCustomers.map((c) => (
          <CustBlock key={c.customerId}>
            <CustHead>
              <Check>
                <input type="checkbox" checked={c.active} disabled={savingAct} onChange={(e) => setCustomerActive(c.customerId, e.target.checked)} />
                {c.customerName}
              </Check>
              {c.employees.length > 0 && (
                <Check>
                  <BulkCheckbox
                    checked={c.employees.every((e) => e.active)}
                    indeterminate={c.employees.some((e) => e.active) && !c.employees.every((e) => e.active)}
                    disabled={!c.active || savingAct}
                    aria-label={t('admin.invoicing.allEmployees', { customer: c.customerName })}
                    onChange={(e) => setAllEmployeesActive(c.customerId, e.target.checked)}
                  />
                  {t('admin.invoicing.all')}
                </Check>
              )}
            </CustHead>
            {c.employees.map((e) => (
              <EmpRow key={e.employeeId}>
                <Check style={{ fontWeight: 400, fontSize: '0.85rem' }}>
                  <input type="checkbox" checked={e.active} disabled={!c.active || savingAct} onChange={(ev) => setEmployeeActive(c.customerId, e.employeeId, ev.target.checked)} />
                  {e.employeeName}
                </Check>
              </EmpRow>
            ))}
          </CustBlock>
        ))}
        <Btn $variant="ghost" onClick={saveActivation} disabled={savingAct}>
          {savingAct ? t('common.saving') : t('admin.invoicing.saveActivation')}
        </Btn>
      </Card>

      {/* Preview */}
      <Card>
        <CardTitle>{t('admin.invoicing.preview')}</CardTitle>
        <Subtitle>{t('admin.invoicing.selectHint')}</Subtitle>
        {!preview || preview.customers.length === 0 ? (
          <Empty>{t('admin.invoicing.noData')}</Empty>
        ) : (
          <>
            {preview.customers.map((c) => (
              <CustBlock key={c.customerId}>
                <CustHead>
                  <span>{c.customerName}</span>
                  <span>{t('admin.invoicing.customerTotal')}: {c.totalHours} {t('admin.invoicing.hours')}</span>
                </CustHead>
                <div style={{ padding: '0.6rem 1rem' }}>
                  <details onToggle={(event) => { if (event.target.open && !billingHistory[c.customerId]) loadBillingHistory(c.customerId); }}>
                    <summary>CRM billing schedule and revision history</summary>
                    <small>CRM determines invoice prices. Imported price notes are legacy information only.</small>
                    {billingHistory[c.customerId]?.loading && <div>Loading…</div>}
                    {billingHistory[c.customerId]?.error && <div>{billingHistory[c.customerId].error}</div>}
                    {billingHistory[c.customerId] && !billingHistory[c.customerId].loading && !billingHistory[c.customerId].error && <BillingHistory data={billingHistory[c.customerId]} />}
                  </details>
                </div>
                {c.employees.map((e) => (
                  <EmpRow key={e.employeeId}>
                    <EmpHead>
                      <span>{e.employeeName}</span>
                      <span>{t('admin.invoicing.employeeTotal')}: {e.totalHours} h</span>
                    </EmpHead>
                    <EntryScroll><EntryInner>
                    {e.entries.map((en) => (
                      <EntryRow key={en.id}>
                        <input type="checkbox" checked={selected.has(en.id)} onChange={() => toggleEntry(en.id)} />
                        <span>{en.date} {en.previousMonth && <Badge>{t('admin.invoicing.previousBadge')}</Badge>}</span>
                        <span>{en.projectName || '—'}</span>
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{en.hours} h</span>
                        <span />
                      </EntryRow>
                    ))}
                    </EntryInner></EntryScroll>
                  </EmpRow>
                ))}
              </CustBlock>
            ))}
            <Total>{t('admin.invoicing.grandTotal')}: {preview.totalHours} {t('admin.invoicing.hours')}</Total>
            <Actions>
              <Btn $variant="download" onClick={download} disabled={busy || selected.size === 0}>
                <FiDownload /> {busy ? t('admin.invoicing.downloading') : t('admin.invoicing.download')}
              </Btn>
              <Btn onClick={push} disabled={busy || selected.size === 0}>
                <FiUploadCloud /> {busy ? t('admin.invoicing.pushing') : preview?.billingMode === 'legacy' ? t('admin.invoicing.push') : 'Prepare invoices for review'}
              </Btn>
            </Actions>
          </>
        )}

        {prepared && <div style={{ marginTop: '1rem' }}>
          <CardTitle>Review prepared invoices</CardTitle>
          <Subtitle>These prices and revisions are saved. Creating drafts rechecks the work and CRM revisions; a change requires a new review.</Subtitle>
          {(prepared.results || []).map((row) => <CustBlock key={row.customerId}>
            <CustHead>{row.customerName || `Customer ${row.customerId}`}</CustHead>
            {!row.ok ? <Alert $type="error">{row.message}{(row.blockers || []).map((blocker) => <div key={blocker.code}>{blocker.message} ({blocker.code})</div>)}</Alert> : <div style={{ padding: '0.75rem' }}>
              {row.lines.map((line, index) => <div key={index} style={{ marginBottom: '0.5rem' }}>
                <div>{line.description}: {line.quantity} × {line.unitNetPrice} {row.currency}</div>
                <small>Revision: {(line.revisionIds || []).join(', ')}</small>
              </div>)}
              {row.settlementMonth && <div>Monthly settlement: {row.settlementMonth}</div>}
              <small>Saved preparation: {row.preparationId}</small>
            </div>}
          </CustBlock>)}
          <Btn onClick={executeReviewed} disabled={busy || !prepared.results.some((row) => row.ok)}>Create reviewed drafts in e-conomic</Btn>
        </div>}

        {results && (
          <div style={{ marginTop: '1rem' }}>
            <CardTitle>{t('admin.invoicing.results')}</CardTitle>
            <div style={{ color: 'var(--ve-ink-600)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
              {t('admin.invoicing.pushedSummary', { pushed: results.pushed, failed: results.failed })}
            </div>
            {results.results.map((r) => (
              <ResultRow key={r.customerId} $ok={r.ok}>
                {r.ok ? <FiCheckCircle /> : <FiXCircle />}
                {r.ok
                  ? t('admin.invoicing.resultOk', { customer: r.customerName, entries: r.entries })
                  : t('admin.invoicing.resultFail', { customer: r.customerName, message: r.message })}
              </ResultRow>
            ))}
          </div>
        )}
      </Card>

      {syncData && (
        <SyncMatchDialog
          provider="economic"
          preview={syncData}
          busy={syncBusy}
          error={syncError}
          onConfirm={confirmSync}
          onClose={() => { setSyncData(null); setSyncError(null); }}
        />
      )}
    </Page>
  );
};

export default Invoicing;
